import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  RefreshControl,
  DeviceEventEmitter,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import ScreenWrapper from '../../Components/ScreenWrapper';
import Header from '../../Components/Header';
import Loading from '../../Components/Loading';
import PostCard from '../../Components/PostCard';
import { fetchDealPosts } from '../../services/postService';
import { useAuth } from '../../Utils/AuthProvider';
import { theme } from '../../constants/theme';

const PAGE_SIZE = 10;

const Deals = () => {
  const { user } = useAuth();
  const PostCardComponent: any = PostCard;
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [authorAdminMap, setAuthorAdminMap] = useState<Record<string, boolean>>({});
  const [communityLinkAllowedMap, setCommunityLinkAllowedMap] = useState<Record<string, boolean>>({});

  const loadDeals = async (isLoadMore = false) => {
    if (isLoadMore && !hasMore) return;

    if (!isLoadMore) {
      setLoading(true);
    }

    const res = await fetchDealPosts(PAGE_SIZE, isLoadMore ? cursor : null);

    if (res.success && res.data) {
      const batch = res.data as any[];

      // Update community link-allowed map
      setCommunityLinkAllowedMap(prev => {
        const next = { ...prev };
        batch.forEach((post: any) => {
          const communityId = String(post?.community_id || post?.communityId || '');
          const isAllowed = post?.community && typeof (post.community as any).is_linkallowed === 'boolean'
            ? Boolean((post.community as any).is_linkallowed)
            : undefined;
          if (communityId && typeof isAllowed === 'boolean' && next[communityId] === undefined) {
            next[communityId] = isAllowed;
          }
        });
        return next;
      });

      // Batch-check admin status for (author, community) pairs
      try {
        const pairs: { key: string; userId: string; communityId: string }[] = [];
        const seen = new Set<string>();

        batch.forEach((post: any) => {
          const userId = String(post?.userId || post?.user_id || post?.user?.id || '');
          const communityId = String(post?.community_id || post?.communityId || '');
          if (!userId || !communityId) return;
          const key = `${userId}|${communityId}`;
          if (authorAdminMap[key] !== undefined || seen.has(key)) return;
          seen.add(key);
          pairs.push({ key, userId, communityId });
        });

        if (pairs.length > 0) {
          const results = await Promise.all(
            pairs.map(async ({ key, userId, communityId }) => {
              try {
                const { data, error } = await Supabase.rpc('check_user_community_admin', {
                  user_uuid: userId,
                  community_uuid: communityId,
                });
                if (error) {
                  return { key, isAdmin: false };
                }
                return { key, isAdmin: Boolean(data) };
              } catch {
                return { key, isAdmin: false };
              }
            })
          );

          setAuthorAdminMap(prev => {
            const next = { ...prev };
            results.forEach(r => {
              next[r.key] = r.isAdmin;
            });
            return next;
          });
        }
      } catch {
        // Safe fallback
      }

      if (isLoadMore) {
        setDeals(prev => {
          const existingIds = new Set(prev.map(post => post.id));
          const newPosts = batch.filter(post => !existingIds.has(post.id));
          return [...prev, ...newPosts];
        });
      } else {
        setDeals(batch);
      }

      if (res.hasMore !== undefined) setHasMore(res.hasMore);
      if (res.nextCursor !== undefined) setCursor(res.nextCursor);
    }

    setLoading(false);
    setRefreshing(false);
  };

  useFocusEffect(
    useCallback(() => {
      setCursor(null);
      setHasMore(true);
      loadDeals(false);

      return () => {
        try { DeviceEventEmitter.emit('pauseAllVideos'); } catch (_) {}
      };
    }, [])
  );

  const onRefresh = async () => {
    setRefreshing(true);
    setCursor(null);
    setHasMore(true);
    await loadDeals(false);
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      loadDeals(true);
    }
  };

  return (
    <ScreenWrapper bg="white">
      <Header title="Deals" />
      <View style={styles.container}>
        {loading && deals.length === 0 ? (
          <View style={styles.center}>
            <Loading />
          </View>
        ) : deals.length === 0 ? (
          <View style={styles.center}>
            <Text style={styles.empty}>No deals available right now.</Text>
            <Text style={styles.helper}>Check back later for fresh offers from admins.</Text>
          </View>
        ) : (
          <FlatList
            data={deals}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item }) => {
              const communityId = String((item as any)?.community_id || (item as any)?.communityId || '');
              const userId = String((item as any)?.userId || (item as any)?.user_id || (item as any)?.user?.id || '');
              const adminKey = `${userId}|${communityId}`;
              const allowLinks =
                Boolean(communityId && communityLinkAllowedMap[communityId]) &&
                Boolean(userId && authorAdminMap[adminKey]);

              return (
                <PostCardComponent
                  item={item}
                  currentUser={user}
                  showMoreIcon
                  hasShadow
                  showReportSpam
                  allowLinks={allowLinks}
                />
              );
            }}
            contentContainerStyle={styles.listContent}
            onEndReached={loadMore}
            onEndReachedThreshold={0.5}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListFooterComponent={
              hasMore ? (
                <View style={{ paddingVertical: 24 }}>
                  <Loading />
                </View>
              ) : null
            }
          />
        )}
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  empty: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    textAlign: 'center',
  },
  helper: {
    marginTop: 8,
    fontSize: 14,
    color: theme.colors.textLight,
    textAlign: 'center',
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
});

export default Deals;

